import { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/App";
import { Button } from "@/components/ui/button";
import { 
  Settings, 
  FileText, 
  Package, 
  Shield, 
  LogOut, 
  Menu, 
  X,
  Laptop,
  Building2
} from "lucide-react";

export default function Sidebar({ currentPath }) {
  const { user, organization, logout } = useAuth();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  const navItems = [
    { path: "/configure", label: "Configure", icon: Laptop },
    { path: "/dashboard?view=assets", label: "My Assets", icon: Package },
    { path: "/dashboard?view=quotes", label: "My Quotes", icon: FileText },
  ];

  const adminItems = user?.is_admin ? [
    { path: "/admin", label: "Admin Panel", icon: Shield },
  ] : [];

  const allItems = [...navItems, ...adminItems];

  const SidebarContent = () => (
    <div className="flex flex-col h-full bg-card border-r border-border">
      {/* Logo */}
      <div className="p-5 border-b border-border">
        <div className="flex items-center gap-2">
          <img 
            src="https://customer-assets.emergentagent.com/job_itpro-shop/artifacts/6nsrlqnk_Gemini_Generated_Image_wzvx61wzvx61wzvx.png" 
            alt="OneIT" className="w-10 h-10 object-contain" 
          />
          <span className="font-heading font-bold text-lg text-foreground">OneIT</span>
        </div>
        {organization && (
          <div className="mt-3 flex items-center gap-2 text-xs text-muted-foreground">
            <Building2 className="w-3 h-3" />
            <span className="truncate">{organization.name}</span>
          </div>
        )}
      </div>

      {/* Navigation */}
      <nav className="flex-1 p-3 space-y-1">
        {allItems.map((item) => (
          <NavLink
            key={item.path}
            to={item.path}
            onClick={() => setMobileOpen(false)}
            className={({ isActive }) =>
              `sidebar-link flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all ${
                isActive || currentPath?.startsWith(item.path.split("?")[0])
                  ? "bg-[#007AFF]/8 text-[#007AFF] font-semibold"
                  : "text-muted-foreground hover:bg-secondary hover:text-foreground"
              }`
            }
            data-testid={`nav-${item.label.toLowerCase().replace(/\s/g, "-")}`}
          >
            <item.icon className="w-4 h-4" />
            {item.label}
          </NavLink>
        ))}
      </nav>

      {/* User info */}
      <div className="p-4 border-t border-border">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-8 h-8 rounded-full bg-[#007AFF]/10 flex items-center justify-center text-sm font-semibold text-[#007AFF]">
            {user?.name?.[0]?.toUpperCase() || "U"}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-foreground truncate">{user?.name}</p>
            <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
          </div>
        </div>
        <Button
          variant="ghost"
          size="sm"
          className="w-full justify-start text-muted-foreground hover:text-destructive"
          onClick={handleLogout}
          data-testid="logout-btn"
        >
          <LogOut className="w-4 h-4 mr-2" />
          Sign Out
        </Button>
      </div>
    </div>
  );

  return (
    <>
      {/* Mobile toggle */}
      <button
        className="md:hidden fixed top-4 left-4 z-50 p-2 rounded-lg bg-card border border-border shadow-sm"
        onClick={() => setMobileOpen(!mobileOpen)}
        data-testid="mobile-menu-toggle"
      >
        {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
      </button>

      {/* Mobile overlay */}
      {mobileOpen && (
        <div 
          className="md:hidden fixed inset-0 bg-black/20 backdrop-blur-sm z-40"
          onClick={() => setMobileOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed md:sticky top-0 left-0 h-screen w-64 z-40 transition-transform duration-300 ${
          mobileOpen ? "translate-x-0" : "-translate-x-full md:translate-x-0"
        }`}
      >
        <SidebarContent />
      </aside>
    </>
  );
}
